import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import * as Styles from './ModalApplications.styles';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useLocale from '@/hooks/useLocale';

import { useHistory } from 'react-router';
import { Radio, TextField } from 'gatorcomponents';
import { locale } from '@/utils/locale';
import { formatAddonsList } from '@/utils/Formatters/formatAddonsList';
import { billingAnalytics } from '@/analytics';

import VpsStoreModal from '@/pages/Products/VpsOciDetailPage/pages/ProductTemplatesPage/commons/VpsStoreModal';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils';

import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

const ModalApplications = ({
  onClose,
  selectedItem,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const initialCheckedOption = useMemo(() => ({ id: 'alreadyHasDomain' }), []);

  const [modalType, setModalType] = useState('selectionApplication');
  const [bodyData, setBodyData] = useState({});
  const [checkedOption, setCheckedOption] = useState({ id: 'alreadyHasDomain' });
  const [domainError, setDomainError] = useState(false);
  const [onBlur, setOnBlur] = useState(false);
  const [requestChangeImage, setRequestChangeImage] = useState(false);

  const vpsOci = useSelector(state => state.vpsOci);
  const { availableOptions, addons, productName } = (vpsOci && vpsOci.data) || {};
  const { applications } = availableOptions || {};
  const changeImageLoading = useSelector(state => state.vpsOci?.changeImage?.loading);

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;

  const { applications: localeVpsOciStoreChangeApplicationsModalApplications } = localeVpsOci.store && localeVpsOci.store.changeApplications && localeVpsOci.store.changeApplications.modal;
  const { applications: localeVpsOciStoreDetailsApplications } = localeVpsOci.store && localeVpsOci.store.details;

  const { alert: localeVpsOciStoreModalAlert } = localeVpsOci && localeVpsOci.store && localeVpsOci.store.modal;

  const { hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;

  const addonsPaidList = useMemo(() => (addons && addons.filter(addon => addon.isPaid)) || [], [addons]);
  const listAddonsNames = useMemo(() => (
    addonsPaidList && addonsPaidList.map(addon => addon.name)
  ), [addonsPaidList]);
  const formattedAddonsText = useMemo(() => formatAddonsList(listAddonsNames), [listAddonsNames]);
  const hasPaidAddons = useMemo(() => (addonsPaidList && addonsPaidList.length > 0), [addonsPaidList]);

  const selectedApplications = useMemo(() => applications && applications.find(panel => selectedItem && panel.slug === selectedItem),
    [applications, selectedItem]);

  const provisionedSo = selectedApplications && selectedApplications.operationalSystems && selectedApplications.operationalSystems.name;

  const domainRequired = selectedApplications ? selectedApplications.domainRequired : false;

  const {
    name: applicationName = '',
    slug: applicationslug = null,
    addonId: selectedAddonId = null,
  } = selectedApplications || {};

  const { id: hostingId } = useParams();

  const getDescriptionBySlug = useCallback((slug) => {
    const item = localeVpsOciStoreDetailsApplications[slug];
    return item ? item.description : null;
  }, [localeVpsOciStoreDetailsApplications]);

  const handleCloseModal = useCallback(() => {
    onClose && onClose();
    setBodyData(null);
    setCheckedOption(initialCheckedOption);

    if (!requestChangeImage) {
      hostingAndServersAnalytics.hostingOsSelectVersionCancelClick(selectedItem, productName);
    }
  }, [onClose, initialCheckedOption, hostingAndServersAnalytics, selectedItem, productName, requestChangeImage]);

  const handleBackStep = useCallback(() => {
    setCheckedOption(initialCheckedOption);

    if (modalType === 'selectionApplication') {
      handleCloseModal();
      hostingAndServersAnalytics.hostingOsSelectApplicationBackClick(selectedItem, productName);
      return;
    }

    if (modalType === 'selectionDomain') {
      setModalType('selectionApplication');
      hostingAndServersAnalytics.hostingOsSelectDomainBackClick(selectedItem, productName);
      return;
    }

    if (modalType === 'confirmWithPanelPaidActive' || modalType === 'confirmWithoutPanelPaidActive') {
      if (domainRequired) {
        setModalType('selectionDomain');
      } else {
        setModalType('selectionApplication');
      }

      if (modalType === 'confirmWithPanelPaidActive') {
        hostingAndServersAnalytics.hostingOsPaymentBackClick(selectedItem, productName);
      } else if (modalType === 'confirmWithoutPanelPaidActive') {
        hostingAndServersAnalytics.hostingOsInstallBackClick(selectedItem, productName);
      }
      return;
    }

    setBodyData(null);
  }, [modalType, handleCloseModal, domainRequired, initialCheckedOption, hostingAndServersAnalytics, selectedItem, productName]);

  const setNextStepConfirm = useCallback(() => {
    if (hasPaidAddons) {
      setModalType('confirmWithPanelPaidActive');
    } else {
      setModalType('confirmWithoutPanelPaidActive');
    }
  }, [hasPaidAddons]);

  const handleStepSelectApplication = useCallback(() => {
    setBodyData({
      hostingId,
      newImageId: selectedAddonId,
      imageType: 'application',
    });

    hostingAndServersAnalytics.hostingOsSelectVersionContinueClick(selectedItem, productName);

    if (domainRequired) {
      setModalType('selectionDomain');
      return;
    }

    setNextStepConfirm();
  }, [hostingId, selectedAddonId, domainRequired, setNextStepConfirm, hostingAndServersAnalytics, selectedItem, productName]);

  const handleSelectOption = useCallback((option) => {
    setCheckedOption((prev) => {
      if (prev.id === option.id) return prev;
      return {
        id: option.id,
        domain: prev.domain || '',
      };
    });
  }, []);

  const domainMask = useCallback((value = '') => value
    .replace(/\s+/g, '')
    .replace(/^www\./i, '')
    .replace(/[^a-zA-Z0-9.-]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^-+/, '')
    .replace(/-+$/, ''),
  []);

  const isValidDomain = useCallback((domain) => {
    if (!domain) return false;
    if (/^www\./i.test(domain)) return false;
    return /^([a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF-]{1,63}\.)+[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]{2,}$/u.test(domain);
  }, []);

  const handleDomainInputChange = useCallback((e) => {
    setOnBlur(false);
    let value = (e && e.target && e.target.value) || '';
    value = value.replace(/^www\./i, '');
    setCheckedOption(prev => ({
      ...prev,
      domain: value,
    }));
    setDomainError(!!value && !isValidDomain(value));
  }, [isValidDomain]);

  const handleStepSelectDomain = useCallback(() => {
    if (checkedOption.id === 'needToRegisterDomain') {
      hostingAndServersAnalytics.hostingOsRegisterDomainContinueClick(selectedItem, productName);
      history.push(`${locale('routes.domains')}${locale('routes.buyDomain')}`);
      return;
    }

    let domain = checkedOption.domain || '';
    if (domain && !/^www\./i.test(domain)) {
      domain = `www.${domain}`;
    }

    setBodyData({
      ...bodyData,
      domain,
    });

    hostingAndServersAnalytics.hostingOsSelectDomainContinueClick(selectedItem, productName);

    setNextStepConfirm();
  }, [bodyData, setNextStepConfirm, checkedOption, history, hostingAndServersAnalytics, selectedItem, productName]);

  const handleStepConfirm = useCallback(() => {
    if (hostingId) {
      setRequestChangeImage(true);
      dispatch(vpsOciActions.changeImage.request(bodyData));

      hostingAndServersAnalytics.hostingOsInstallConfirmClick(selectedItem, productName);
    }
  }, [bodyData, hostingId, dispatch, hostingAndServersAnalytics, selectedItem, productName]);

  useEffect(() => {
    if (requestChangeImage && !changeImageLoading) {
      handleCloseModal();
      setRequestChangeImage(false);
    }
  }, [changeImageLoading, handleCloseModal, requestChangeImage]);

  const modalApplicationsDetails = useMemo(() => {
    const icon = applicationslug ? getIconBySlug(applicationslug) : null;
    const description = applicationslug ? getDescriptionBySlug(applicationslug) : null;

    const selectedSOInfoData = {
      name: applicationName,
      description: description || '',
      icon: icon ? (
        <Styles.SelectedApplicationTitleIcon
          src={icon}
          alt={applicationName}
          data-testid={`vpsoci-templates-application-icon-${applicationslug}`}
        />
      ) : null,
    };

    if (!selectedSOInfoData) return null;

    return (
      <Styles.SelectedApplication data-testid="vpsoci-templates-application">
        <Styles.SelectedApplicationTitle data-testid="vpsoci-templates-application-name">
          {selectedSOInfoData.icon}
          {selectedSOInfoData.name}
        </Styles.SelectedApplicationTitle>
        <Styles.SelectedApplicationDescription data-testid="vpsoci-templates-application-description">
          {selectedSOInfoData.description}

          {localeVpsOciStoreChangeApplicationsModalApplications.provisioned && (
            <Styles.ProvisionedText>
              {localeVpsOciStoreChangeApplicationsModalApplications.provisioned(provisionedSo)}
            </Styles.ProvisionedText>
          )}
        </Styles.SelectedApplicationDescription>
      </Styles.SelectedApplication>
    );
  }, [
    getDescriptionBySlug,
    applicationslug,
    applicationName,
    localeVpsOciStoreChangeApplicationsModalApplications,
    provisionedSo,
  ]);

  const modalApplicationsDetailsDomain = useMemo(() => (
    <Styles.RequestDomainWrapper>
      <Styles.RequestDomainDescription data-testid="vpsoci-templates-application-domain-description">
        {localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.message}
      </Styles.RequestDomainDescription>
      <Styles.RequestDomainInputList>
        <Styles.RequestDomainInputListItem
          data-testid="alreadyHasDomain"
          htmlFor="alreadyHasDomain"
          onClick={() => handleSelectOption({ id: 'alreadyHasDomain' })}
          role="button"
          $checked={checkedOption.id === 'alreadyHasDomain'}
        >
          <Styles.RequestDomainInputListItemRadio>
            <Radio
              id="alreadyHasDomain"
              variant="blue"
              checked={checkedOption.id === 'alreadyHasDomain'}
            />
          </Styles.RequestDomainInputListItemRadio>

          <Styles.RequestDomainInputListItemCollapse>
            <Styles.RequestDomainInputListItemLabel>
              {localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.alreadyHasDomain.label}
            </Styles.RequestDomainInputListItemLabel>
            <Styles.RequestDomainInputListItemContent
              $checked={checkedOption.id === 'alreadyHasDomain'}
            >
              <TextField
                id="vpsoci-already-has-domain-input"
                type="text"
                label={localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.alreadyHasDomain.input.label}
                value={checkedOption.domain}
                status={domainError ? 'error' : 'default'}
                helpText={
                  (domainError && onBlur)
                    ? localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.alreadyHasDomain.input.errorMessage
                    : localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.alreadyHasDomain.input.helpText
                }
                onChange={handleDomainInputChange}
                onBlur={() => setOnBlur(true)}
                onFocusInput={() => setOnBlur(false)}
                customMask={domainMask}
                fullWidth
              />
            </Styles.RequestDomainInputListItemContent>
          </Styles.RequestDomainInputListItemCollapse>
        </Styles.RequestDomainInputListItem>
        <Styles.RequestDomainInputListItem
          data-testid="needToRegisterDomain"
          htmlFor="needToRegisterDomain"
          onClick={() => handleSelectOption({ id: 'needToRegisterDomain' })}
          role="button"
          $checked={checkedOption.id === 'needToRegisterDomain'}
        >
          <Styles.RequestDomainInputListItemRadio>
            <Radio
              id="needToRegisterDomain"
              variant="blue"
              checked={checkedOption.id === 'needToRegisterDomain'}
            />
          </Styles.RequestDomainInputListItemRadio>

          <Styles.RequestDomainInputListItemCollapse>
            <Styles.RequestDomainInputListItemLabel>
              {localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.needToRegisterDomain.label}
            </Styles.RequestDomainInputListItemLabel>
            <Styles.RequestDomainInputListItemContent
              $checked={checkedOption.id === 'needToRegisterDomain'}
            >
              {localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.needToRegisterDomain.description}
            </Styles.RequestDomainInputListItemContent>
          </Styles.RequestDomainInputListItemCollapse>
        </Styles.RequestDomainInputListItem>
      </Styles.RequestDomainInputList>
    </Styles.RequestDomainWrapper>
  ), [
    checkedOption,
    localeVpsOciStoreChangeApplicationsModalApplications,
    handleDomainInputChange,
    domainMask,
    domainError,
    handleSelectOption,
    onBlur,
  ]);

  const modalConfig = useMemo(() => ({
    selectionApplication: {
      title: localeVpsOciStoreChangeApplicationsModalApplications.title,
      message: modalApplicationsDetails,
      alert: null,
      testId: 'vpsoci-applications-selectionApplication-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.selectionApplication.confirmButton,
        action: handleStepSelectApplication,
        testId: 'vpsoci-applications-selectionApplication-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.selectionApplication.closeButton,
        action: handleCloseModal,
        testId: 'vpsoci-applications-selectionApplication-modal-close-button',
      },
    },
    selectionDomain: {
      title: localeVpsOciStoreChangeApplicationsModalApplications.title,
      message: modalApplicationsDetailsDomain,
      alert: null,
      testId: 'vpsoci-applications-selectionDomain-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.confirmButton,
        action: handleStepSelectDomain,
        disabled: !checkedOption.id
          || (checkedOption.id === 'alreadyHasDomain' && (!checkedOption.domain || domainError)),
        testId: 'vpsoci-applications-selectionDomain-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.selectionDomain.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-applications-selectionDomain-modal-close-button',
      },
    },
    confirmWithoutPanelPaidActive: {
      title: localeVpsOciStoreChangeApplicationsModalApplications.title,
      message: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithoutPanelPaidActive.message(applicationName),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithoutPanelPaidActive.agreedTerms(applicationName),
      testId: 'vpsoci-applications-confirmWithoutPanelPaidActive-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithoutPanelPaidActive.confirmButton,
        action: handleStepConfirm,
        loading: changeImageLoading,
        testId: 'vpsoci-applications-confirmWithoutPanelPaidActive-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithoutPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-applications-confirmWithoutPanelPaidActive-modal-close-button',
      },
    },
    confirmWithPanelPaidActive: {
      title: localeVpsOciStoreChangeApplicationsModalApplications.title,
      message: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithPanelPaidActive.message(applicationName, formattedAddonsText),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithPanelPaidActive.agreedTerms(applicationName, formattedAddonsText),
      testId: 'vpsoci-applications-confirmWithPanelPaidActive-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithPanelPaidActive.confirmButton,
        action: handleStepConfirm,
        loading: changeImageLoading,
        testId: 'vpsoci-applications-confirmWithPanelPaidActive-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalApplications.confirmWithPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-applications-confirmWithPanelPaidActive-modal-close-button',
      },
    },
  }), [
    handleBackStep,
    handleCloseModal,
    handleStepConfirm,
    handleStepSelectApplication,
    handleStepSelectDomain,
    localeVpsOciStoreChangeApplicationsModalApplications,
    localeVpsOciStoreModalAlert,
    modalApplicationsDetails,
    modalApplicationsDetailsDomain,
    applicationName,
    formattedAddonsText,
    checkedOption,
    domainError,
    changeImageLoading,
  ]);

  if (!selectedItem || !applications || Object.keys(applications).length === 0) return null;

  return (
    <VpsStoreModal
      modalType={modalType}
      modalConfig={modalConfig}
      onClose={handleCloseModal}
    />
  );
};

export default ModalApplications;
